'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { setBannerAsDismissed } from '@/ui/components/layout/banner/banner-utils';
import { BannerContextType } from './types/IBannerContext';

const BannerContext = createContext<BannerContextType>({
  isBannerClosed: false,
  setIsBannerClosed: () => {},
});

export function BannerProvider({
  children,
  bannerId,
  initialDismissed = false,
}: Readonly<{ children: React.ReactNode; bannerId?: string; initialDismissed?: boolean }>) {
  // Seeded from the dismissal cookie the server already read, so SSR and the first client render
  // agree — a previously dismissed banner never flashes in before hiding.
  const [isBannerClosed, setIsBannerClosed] = useState(initialDismissed);

  // Persist the dismissal so it survives reloads and future visits.
  useEffect(() => {
    if (isBannerClosed && bannerId) {
      setBannerAsDismissed(bannerId);
    }
  }, [isBannerClosed, bannerId]);

  const bannerContextValue = useMemo(
    () => ({ isBannerClosed, setIsBannerClosed }),
    [isBannerClosed],
  );

  return <BannerContext.Provider value={bannerContextValue}>{children}</BannerContext.Provider>;
}

export function useBanner() {
  return useContext(BannerContext);
}
